/* src/App.jsx - Fixed Version with Proper Sidebar Visibility */
import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import LoadingSpinner from './components/UI/LoadingSpinner';
import NetworkStatus from './components/UI/NetworkStatus';

// Lazy load components for better performance
const AuthWrapper = lazy(() => import('./components/Auth/AuthWrapper'));
const Header = lazy(() => import('./components/UI/Header'));
const Sidebar = lazy(() => import('./components/UI/Sidebar'));
const ChatWindow = lazy(() => import('./components/Chat/ChatWindow'));
const NewsPage = lazy(() => import('./components/News/NewsPage'));

// Custom hook for responsive behavior
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 769 : false;
  });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 769);
    };

    const debouncedResize = debounce(checkScreenSize, 100);
    window.addEventListener('resize', debouncedResize);
    
    // Initial check
    checkScreenSize();

    return () => window.removeEventListener('resize', debouncedResize);
  }, []);

  return { isMobile };
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Custom hook for local storage with error handling
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage key "${key}":`, error);
    }
  }, [key]);

  return [storedValue, setValue];
};

// Page transition variants for smooth animations
const pageVariants = {
  initial: { opacity: 0, x: -20 },
  in: { opacity: 1, x: 0 },
  out: { opacity: 0, x: 20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.3
};

// Enhanced Theme Toggle Button Component
const ThemeToggleButton = () => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <motion.button
      className="theme-toggle-btn"
      onClick={toggleTheme}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={`Switch to ${theme.isDark ? 'light' : 'dark'} mode`}
    >
      <motion.div
        initial={false}
        animate={{ rotate: theme.isDark ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        {theme.isDark ? '☀️' : '🌙'}
      </motion.div>
    </motion.button>
  );
};

// Main App Layout Component
const AppLayout = ({ children }) => {
  const { isMobile } = useResponsive();
  const { theme } = useTheme();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useLocalStorage('sidebar-open', !isMobile);

  // Auto-close sidebar on mobile when navigating
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile, setIsSidebarOpen]);

  // Toggle sidebar function
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, [setIsSidebarOpen]);

  // Close sidebar function
  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, [setIsSidebarOpen]);

  // Sidebar variants for animation
  const sidebarVariants = {
    open: { 
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    closed: { 
      x: isMobile ? "-100%" : "0",
      transition: { type: "spring", stiffness: 300, damping: 30 }
    }
  };

  // Overlay variants for mobile
  const overlayVariants = {
    open: { opacity: 1, visibility: "visible" },
    closed: { opacity: 0, visibility: "hidden" }
  };

  return (
    <div className={`app-container ${theme.isDark ? 'dark-theme' : ''}`}>
      {/* Header */}
      <ErrorBoundary>
        <Suspense fallback={<div style={{ height: '60px', background: 'var(--color-surface)' }} />}>
          <Header 
            toggleSidebar={toggleSidebar}
            isSidebarOpen={isSidebarOpen}
            isMobile={isMobile}
          />
        </Suspense>
      </ErrorBoundary>

      <div className="app-content">
        {/* Sidebar */}
        <motion.div
          className={`sidebar-container ${
            isMobile ? 'sidebar-mobile' : 'sidebar-desktop'
          } ${isMobile && isSidebarOpen ? 'sidebar-open' : ''}`}
          variants={isMobile ? sidebarVariants : {}}
          animate={isMobile ? (isSidebarOpen ? "open" : "closed") : "open"}
          initial={isMobile ? "closed" : "open"}
        >
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Sidebar 
                closeMobileSidebar={closeSidebar}
                isMobile={isMobile}
              />
            </Suspense>
          </ErrorBoundary>
        </motion.div>

        {/* Mobile Overlay */}
        {isMobile && (
          <motion.div
            className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`}
            variants={overlayVariants}
            animate={isSidebarOpen ? "open" : "closed"}
            onClick={closeSidebar}
            initial="closed"
          />
        )}

        {/* Main Content Area */}
        <main className="main-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              className="page-container"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Network Status Indicator */}
      <NetworkStatus />
      
      {/* Theme Toggle Button */}

    </div>
  );
};

// Main App Content Component
const AppContent = () => {
  const { currentUser, loading } = useAuth();

  // Enhanced loading state
  if (loading) {
    return (
      <motion.div
        className="loading-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="loading-content"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <LoadingSpinner size="large" />
          <motion.p
            className="loading-text"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Loading AI Chat...
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  // Authentication check
  if (!currentUser) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner size="large" />}>
          <AuthWrapper />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Main app content with chat provider
  return (
    <ChatProvider>
      <AppLayout>
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<ChatWindow />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="*" element={
                <motion.div
                  className="empty-state"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div>🔍</div>
                  <h2>Page Not Found</h2>
                  <p>The page you're looking for doesn't exist.</p>
                </motion.div>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </ChatProvider>
  );
};

// Root App Component with Providers
const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;